import React, { useState } from 'react';
import {
  Grid,
  IconButton,
  TextField as AutoCompleteTextField,
  Typography,
  InputAdornment,
} from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import { ConfirmDialog, FieldLabel } from '@roc/ui';
import { observer } from 'mobx-react-lite';
import BorrowerEntityStore from '../../../stores/documentForms/borrowerEntity/borrowerEntityStore';
import { isFundedLoan } from '@roc/feature-utils';

interface BorrowerEntitySearchProps {
  store: BorrowerEntityStore;
  disabled?: boolean;
}

interface BorrowerEntitySelection {
  borrowerEntityId: number;
  name: string;
  address?: string;
}

interface AddNewOption {
  isAddNew: true;
  name: string;
}

type SearchOption = BorrowerEntitySelection | AddNewOption;

const toSelection = (entity: any): BorrowerEntitySelection => {
  const company = entity?.company;
  return {
    borrowerEntityId: entity?.borrowerEntityId,
    name: company?.name ?? '',
    address: company?.noMatchGoogleAddress || company?.address || '',
  };
};

const baseFilter = createFilterOptions<BorrowerEntitySelection>({
  matchFrom: 'any',
  stringify: option => option.name,
});

const filterOptions = (
  options: SearchOption[],
  params: any
): SearchOption[] => {
  const filtered = baseFilter(options as BorrowerEntitySelection[], params);
  if (filtered.length === 0 && params.inputValue.trim()) {
    return [{ isAddNew: true, name: params.inputValue.trim() }];
  }
  return filtered;
};

const BorrowerEntitySearch = observer(
  ({ store, disabled }: BorrowerEntitySearchProps) => {
    const fields = store.form.fields;
    const [confirmResetOpen, setConfirmResetOpen] = useState(false);
    const { isInternalPortal, isAttorneyPortal } = store.globalStore;
    const isFundedOrPaidOff = isFundedLoan(store.loanStore?.loanDetails?.status);

    const handleConfirmReset = () => {
      setConfirmResetOpen(false);
      store.clearSelectedEntity();
    };

    const resetAdornment = (isInternalPortal || isAttorneyPortal) && !isFundedOrPaidOff && (
      <InputAdornment position="end">
        <IconButton
          size="small"
          aria-label="Reset"
          onClick={() => setConfirmResetOpen(true)}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      </InputAdornment>
    );

    const confirmDialog = (
      <ConfirmDialog
        open={confirmResetOpen}
        title="Reset Borrower Entity"
        body="Once you reset, the data and the documents of this entity will be removed from the loan. Are you sure you want to continue?"
        handleClose={() => setConfirmResetOpen(false)}
        handleCancel={() => setConfirmResetOpen(false)}
        handleConfirm={handleConfirmReset}
      />
    );

    return (
      <>
        <FieldLabel>Name *</FieldLabel>
        <Autocomplete
          style={{ width: '100%' }}
          id="borrowerEntitySearch"
          options={
            (store.borrowerEntities ?? []).map(toSelection) as SearchOption[]
          }
          freeSolo
          disabled={disabled}
          filterOptions={filterOptions}
          getOptionLabel={(option: any) => {
            if (typeof option === 'string') return option;
            if (option?.isAddNew) return '';
            return option?.name ?? '';
          }}
          value={null}
          inputValue={fields.name.value}
          onChange={(_event, newValue: any) => {
            if (!newValue || typeof newValue === 'string') return;
            if (newValue.isAddNew) {
              store.clearSelectedEntity();
              store.onFieldChange('name', newValue.name);
              store.onFieldChange('type', 'Limited Liability Company');
            } else {
              store.setSelectedBorrowerEntity(newValue.borrowerEntityId);
            }
          }}
          onInputChange={(_event, value, reason) => {
            if (reason === 'input') {
              store.onFieldChange('name', value);
            } else if (reason === 'clear') {
              store.clearSelectedEntity();
            }
          }}
          renderOption={(option: any) => {
            if (option.isAddNew) {
              return (
                <Grid container direction="column" style={{ padding: '4px 0' }}>
                  <Typography
                    style={{ color: 'rgba(0,0,0,0.54)', marginBottom: 8 }}
                  >
                    No matching entities
                  </Typography>
                  <Typography color="primary">
                    {`+ Add "${option.name}" as a new entity`}
                  </Typography>
                </Grid>
              );
            }
            return (
              <Grid container direction="column">
                <Typography variant="body1">{option.name}</Typography>
                {option.address && (
                  <Typography variant="body2" color="textSecondary">
                    {option.address}
                  </Typography>
                )}
              </Grid>
            );
          }}
          renderInput={params => (
            <AutoCompleteTextField
              {...params}
              variant="outlined"
              placeholder="Search or add an entity"
              error={!!fields.name.error}
              helperText={fields.name.error}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: disabled
                  ? resetAdornment
                  : params.InputProps.endAdornment,
              }}
            />
          )}
        />
        {confirmDialog}
      </>
    );
  }
);

export default BorrowerEntitySearch;
